import {
  Collapse,
  InteractiveContainer,
  LoadingSpinner,
  Menu,
  PlainButton,
} from "@jugl-web/ui-components";
import React, { useCallback, useMemo, useState } from "react";
import { cx, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { VList } from "virtua";
import { KnowledgeLinkSourceItem } from "@jugl-web/rest-api/ai-workflows/types";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { getFileSizeLabel } from "@jugl-web/utils/utils/files";
import { useKnowledgeTraining } from "@web-src/modules/cx/providers/KnowledgeTrainingProvider";
import { ReactComponent as WebsiteIcon } from "./assets/website.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as BinIcon } from "./assets/bin.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";
import { ReactComponent as UrlIcon } from "./assets/url.svg";
import { ReactComponent as RecrawlIcon } from "./assets/recrawl.svg";
import { LinkToPreview } from "../../types";
import { useAgentKnowledge } from "../../../../../../../../hooks/useAgentKnowledge";
import { NotTrainedPill } from "./components/NotTrainedPill";

export const LinkKnowledgeItem: React.FC<{
  id: string;
  sourceLink: string;
  links: KnowledgeLinkSourceItem[];
  entityId: string;
  isDisabled: boolean;
  isCrawling: boolean;
  onCrawlingChange: (sourceId?: string) => void;
  onOpenPreview: (linkToPreview: LinkToPreview) => void;
  onDelete: () => void;
  onDeleteParticularLink: (url: string) => void;
}> = ({
  links,
  id,
  sourceLink,
  entityId,
  isDisabled,
  isCrawling,
  onCrawlingChange,
  onDelete,
  onDeleteParticularLink,
  onOpenPreview,
}) => {
  const { t } = useTranslations();
  const { aiWorkflowsApi } = useRestApiProvider();
  const [recrawlLinkKnowledge] =
    aiWorkflowsApi.useRecrawlLinkKnowledgeMutation();
  const [getListLinks] = aiWorkflowsApi.useLazyGetListLinksQuery();
  const [updateLinkKnowledge] = aiWorkflowsApi.useUpdateLinkKnowledgeMutation();
  const { handleCXApiError } = useAgentKnowledge(entityId);
  const { isTotalSizeExceeded } = useKnowledgeTraining();

  const [isOpen, setIsOpen] = useState(false);

  const isIndividualLink = useMemo(
    () => links.length === 1 && sourceLink === links[0].url,
    [links, sourceLink]
  );
  const { isSubscriptionCanceled } = useSubscriptionV2();

  const handleRecrawl = useCallback(async () => {
    onCrawlingChange(id);

    try {
      if (isIndividualLink) {
        await recrawlLinkKnowledge({
          entityId,
          agentId: entityId,
          src_id: id,
          url: sourceLink,
        }).unwrap();
      } else {
        const listLinks = await getListLinks({
          agentId: entityId,
          entityId,
          params: {
            url: sourceLink,
          },
        }).unwrap();

        await updateLinkKnowledge({
          agentId: entityId,
          entityId,
          source: listLinks.links.links,
          id,
        }).unwrap();
      }
    } catch (error) {
      handleCXApiError(error);
    } finally {
      onCrawlingChange(undefined);
    }
  }, [
    onCrawlingChange,
    id,
    isIndividualLink,
    recrawlLinkKnowledge,
    entityId,
    sourceLink,
    getListLinks,
    updateLinkKnowledge,
    handleCXApiError,
  ]);

  return (
    <div className="overflow-hidden rounded-2xl border border-solid border-grey-400">
      <InteractiveContainer
        isDisabled={
          isIndividualLink && (isDisabled || links[0].status === "not_trained")
        }
        onClick={
          !isIndividualLink
            ? () => setIsOpen((prev) => !prev)
            : () => onOpenPreview({ id, type: "knowledge", url: sourceLink })
        }
        className="flex w-full items-center justify-between gap-4 overflow-hidden bg-white p-5"
      >
        <div className="flex min-w-0 items-center justify-between gap-4">
          <div className="flex flex-col gap-1.5 overflow-hidden">
            <div className="flex items-center gap-1.5">
              {isIndividualLink ? (
                <UrlIcon className="shrink-0" />
              ) : (
                <WebsiteIcon className="shrink-0" />
              )}
              <span className="truncate font-secondary leading-4 font-semibold text-dark">
                {sourceLink}
              </span>
              {isIndividualLink && links[0].status === "not_trained" && (
                <NotTrainedPill />
              )}
            </div>
            <span className="font-secondary text-sm text-dark-700">
              <span>
                {isIndividualLink
                  ? t({
                      id: "cx-ai-agents-page.individual-link",
                      defaultMessage: "Individual link",
                    })
                  : t(
                      {
                        id: "cx-ai-agents-page.links-count",
                        defaultMessage:
                          "{count} {count, plural, one {link} other {links}}",
                      },
                      { count: links.length }
                    )}
              </span>
              {isIndividualLink && typeof links[0].size === "number" && (
                <span>
                  {" "}
                  • <span>{getFileSizeLabel(links[0].size)}</span>
                </span>
              )}
              {isCrawling && (
                <span>
                  {" "}
                  •{" "}
                  <span className="font-secondary text-sm text-primary-800">
                    {t({
                      id: "cx-ai-agents-page.crawling",
                      defaultMessage: "Crawling...",
                    })}
                  </span>
                </span>
              )}
            </span>
          </div>
        </div>
        {isCrawling ? (
          <div className="h-8 w-8 rounded-[10px] bg-gradient-to-r from-[#A9BDFF] to-[#F586FF] p-px">
            <div className="flex h-full w-full items-center justify-center rounded-[9px] bg-white">
              <LoadingSpinner size="xs" color="grey" />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            {!isDisabled && !isSubscriptionCanceled && (
              <Menu
                placement="bottom-end"
                autoClose
                sections={[
                  [
                    {
                      id: "re-crawl",
                      label: t({
                        id: "cx-ai-agents-page.recrawl",
                        defaultMessage: "Re-crawl",
                      }),
                      icon: <RecrawlIcon />,
                      onSelect: handleRecrawl,
                      isHidden:
                        isIndividualLink &&
                        (links[0].status === "not_trained" ||
                          isTotalSizeExceeded),
                    },
                    {
                      id: "delete",
                      label: t({
                        id: "common.delete",
                        defaultMessage: "Delete",
                      }),
                      icon: <BinIcon />,
                      onSelect: onDelete,
                    },
                  ],
                ]}
                renderTrigger={({ Trigger, triggerRef }) => (
                  <Trigger
                    ref={triggerRef}
                    as={PlainButton}
                    className="flex h-8 w-8 items-center justify-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreIcon />
                  </Trigger>
                )}
              />
            )}
            {!(isIndividualLink && isDisabled) && (
              <ChevronIcon
                className={cx(
                  "transition-transform",
                  isOpen && "rotate-180",
                  isIndividualLink && "rotate-[270deg]"
                )}
              />
            )}
          </div>
        )}
      </InteractiveContainer>
      <Collapse isOpen={isOpen}>
        {isOpen && (
          <div className="pt-2 pb-7">
            {links.length ? (
              <VList
                overscan={10}
                className="jugl__custom-scrollbar overflow-y-auto px-11"
                style={{
                  height: Math.min(
                    links.length * 36 + (links.length - 1) * 8,
                    300
                  ),
                }}
              >
                {links.map((link, idx) => (
                  <div
                    className={cx(
                      "flex items-center justify-between gap-1.5 px-3 py-1",
                      idx !== 0 && "mt-2"
                    )}
                    key={link.url}
                  >
                    <div className="flex flex-col gap-0.5 truncate">
                      <div className="flex items-center gap-1.5 overflow-hidden">
                        <InteractiveContainer
                          className="truncate font-secondary text-sm text-dark"
                          onClick={() =>
                            onOpenPreview({ id, type: "link", url: link.url })
                          }
                          isDisabled={
                            isDisabled || link.status === "not_trained"
                          }
                        >
                          {link.url}
                        </InteractiveContainer>
                        {link.status === "not_trained" && <NotTrainedPill />}
                      </div>
                      {typeof link.size === "number" && (
                        <span className="font-secondary text-sm leading-[21px] text-dark-600">
                          {getFileSizeLabel(link.size)}
                        </span>
                      )}
                    </div>
                    {!isDisabled && !isSubscriptionCanceled && (
                      <Menu
                        placement="bottom-end"
                        autoClose
                        sections={[
                          [
                            {
                              id: "delete",
                              label: t({
                                id: "cx-ai-agents-page.exclude-url",
                                defaultMessage: "Exclude URL",
                              }),
                              icon: <BinIcon />,
                              onSelect: () => onDeleteParticularLink(link.url),
                            },
                          ],
                        ]}
                        renderTrigger={({ Trigger, triggerRef }) => (
                          <Trigger
                            ref={triggerRef}
                            as={PlainButton}
                            className="flex h-[28px] w-[28px] items-center justify-center"
                          >
                            <MoreIcon />
                          </Trigger>
                        )}
                      />
                    )}
                  </div>
                ))}
              </VList>
            ) : (
              <div className="mx-5 flex h-12 w-max items-center justify-center gap-3 rounded-xl bg-grey-100 px-3">
                <InfoIcon />
                <span className="font-secondary text-sm text-dark">
                  {t({
                    id: "cx-ai-agents-page.no-links-found",
                    defaultMessage: "No links found",
                  })}
                </span>
              </div>
            )}
          </div>
        )}
      </Collapse>
    </div>
  );
};
